import { LoadingArea } from "@/modules/irohana-components/elements/Loading/Loading";
import { Box, Typography } from "@mui/material";
import { Modal } from "@/modules/irohana-components/elements/Modal";
import { DropZone, TDropZoneProps } from "@/modules/irohana-components/elements/DropZone";
import { FCC } from "@/types/common";
import { ReactNode, useState } from "react";
import { TExtendFile } from "@/types/file";
import { MAX_SIZE_ERROR, TFileUploadFor } from "@/constants/file";
import { divider } from "@/theme/colors";
import { rounded } from "@/theme/variables";
import { toast } from "@/utils/toast";

type TUploadPopupProps = {
  title?: ReactNode;
  isOpen: boolean;
  onClose: () => void;
  onDropFiles?: (files: TExtendFile[]) => Promise<boolean>; //isError
  onFinishUpload: (files: TExtendFile[]) => void;
  onCancel?: () => void;
  isLoading?: boolean;
  uploadFor: TFileUploadFor;
} & Omit<TDropZoneProps, "enableFileList">;

export const UploadFile: FCC<TUploadPopupProps> = ({
  children,
  title,
  isOpen,
  onClose,
  onDropFiles,
  rules = {},
  isLoading = false,
  onFinishUpload,
  // Deliberately not forwarded to DropZone: the module DropZone uploads the file immediately when
  // `uploadFor` is set, while the form still sends the raw File on submit — that would upload twice.
  uploadFor: _uploadFor,
  ...rest
}) => {
  const [, setDropFiles] = useState<TExtendFile[]>([]);

  const handleChangeFiles = async (value: TExtendFile | TExtendFile[] | null) => {
    let files = [value ?? []].flat();
    if (!files.length) return;
    let maxSizeError = false;
    files = [files[0]];
    // console.log(files);
    files.forEach((file) => {
      const { maxSize } = rules;
      const { size } = file;
      if (maxSize && size > maxSize) {
        maxSizeError = true;
        toast.error(MAX_SIZE_ERROR(maxSize));
        onClose();
        return;
      }
    });
    if (maxSizeError) return;
    onFinishUpload(files);
    const isError = await onDropFiles?.(files);
    if (isError) return;
    setDropFiles(files);
    onClose();
  };
  return (
    <>
      <Modal title={title} isOpen={isOpen} onClose={onClose} enableFooter={false} enableCloseIcon={!isLoading}>
        <Box
          mt={2}
          mb={3}
          p={3}
          border="1px solid"
          borderColor={divider.high}
          borderRadius={rounded.sm}
          // The module DropZone has no cursor rule of its own (the app-local one does), so the drop area
          // gives no hover feedback. Scope the hand cursor to the DropZone subtree, not the padding ring.
          sx={isLoading ? undefined : { "& > div": { cursor: "pointer" } }}
        >
          {isLoading ? (
            <LoadingArea />
          ) : (
            <DropZone {...rest} enableFileList={false} onChange={handleChangeFiles} nonControl rules={rules}>
              {children ?? <Typography variant="body14" whiteSpace="pre-wrap" textAlign="center">{`ファイルを\nドラッグ&ドロップ`}</Typography>}
            </DropZone>
          )}
        </Box>
      </Modal>
    </>
  );
};
